// Packages:
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Position } from 'react-flow-renderer';
import simplur from 'simplur';
import hasNodeMoved from '../../../../lib/hasNodeMoved';

// Redux:
import { setSelectedNode } from '../../../../redux/actions/studioActions';

// Styles:
import {
  APINodeWrapper,
  APINodeLabel,
  Body,
  Description,
  Field,
  StyledHandle
} from './styles';

// Functions:
const APINode = ({ data }) => {
  // Constants:
  const dispatch = useDispatch();
  const { id, inputs, endpoint, label } = data;

  // Ref:
  const wrapperRef = useRef(null);

  // State:
  const studio = useSelector((state) => state.studio);
  const [clickOrigin, setClickOrigin] = useState({ x: 0, y: 0 });

  // Functions:
  const handleMouseDown = useCallback(
    (e) => {
      if (e.button === 2) e.stopPropagation();
      if (e.button === 0) {
        setClickOrigin({ x: e.clientX, y: e.clientY });
        dispatch(setSelectedNode(data));
      }
    },
    [data, dispatch]
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (e.button === 0) {
        if (hasNodeMoved(clickOrigin, e)) return;
      }
    },
    [clickOrigin]
  );

  // Effects:
  useEffect(() => {
    wrapperRef.current.addEventListener('mousedown', handleMouseDown);
    wrapperRef.current.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseDown, handleMouseUp, clickOrigin, studio.selectedNode]);

  // Return:
  return (
    <APINodeWrapper
      ref={wrapperRef}
      style={{
        backgroundColor: studio.selectedNode?.id === id ? '#7721E8' : '#FFFFFF',
        borderColor: '#7721E8'
      }}
    >
      <StyledHandle
        type="target"
        position={Position.Top}
        style={{ backgroundColor: 'blue', top: '-15px' }}
      />
      <StyledHandle
        id="api-node-output-true"
        type="source"
        position={Position.Bottom}
        style={{
          backgroundColor: 'green',
          left: '25%',
          bottom: '-15px'
        }}
      />
      <StyledHandle
        id="api-node-output-false"
        type="source"
        position={Position.Bottom}
        style={{
          backgroundColor: 'red',
          left: '75%',
          bottom: '-15px'
        }}
      />
      <APINodeLabel
        htmlFor="text"
        style={{
          color: studio.selectedNode?.id === id ? '#FFFFFF' : '#333533'
        }}
      >
        {label}
      </APINodeLabel>
      <Body
        style={{
          color: studio.selectedNode?.id === id ? '#FFFFFF' : '#333533'
        }}
      >
        <Description>
          Takes{' '}
          <Field isActive={studio.selectedNode?.id === id}>
            {inputs.length === 0 ? (
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                variableName
              </span>
            ) : (
              inputs.slice(0, 3).map((input, i) => (
                <span key={`${input.key}-var-${i}`}>
                  {input.key.length === 0 ? (
                    <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                      variableName
                    </span>
                  ) : input.key.length >= 10 ? (
                    `${input.key.slice(0, 10).trim()}..`
                  ) : (
                    input.key
                  )}
                  {(i === 0 && input.key.length !== 0) || i < 2 ? ', ' : ''}
                </span>
              ))
            )}
            {inputs.length > 3 &&
              simplur`, and ${inputs.length - 3} more variabl[e|es]`}
          </Field>
          {simplur`${[inputs.length]} as inpu[t|ts]`}
        </Description>
        <Description>
          Sends to{' '}
          <Field isActive={studio.selectedNode?.id === id}>
            {endpoint.length === 0 ? (
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                endpointName
              </span>
            ) : endpoint.length >= 25 ? (
              `${endpoint.slice(0, 25).trim()}..`
            ) : (
              endpoint
            )}
          </Field>{' '}
          endpoint
        </Description>
      </Body>
    </APINodeWrapper>
  );
};

// Exports:
export default APINode;
